// Minimal Accordion primitive matching the shadcn/ui API surface
// (Accordion / AccordionItem / AccordionTrigger / AccordionContent) so callers
// look identical to the canonical shadcn block. Hand-rolled because
// `@radix-ui/react-accordion` is not installed in this env (npm install is
// blocked); swap to the radix-backed shadcn version when deps are available.

import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

type AccordionType = 'single' | 'multiple';

interface AccordionContextValue {
  type: AccordionType;
  openValues: string[];
  toggle: (value: string) => void;
  collapsible: boolean;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

function useAccordion() {
  const ctx = React.useContext(AccordionContext);
  if (!ctx) throw new Error('Accordion subcomponents must be used within <Accordion>.');
  return ctx;
}

interface AccordionRootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue'> {
  type?: AccordionType;
  collapsible?: boolean;
  defaultValue?: string | string[];
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionRootProps>(
  ({ type = 'single', collapsible = true, defaultValue, className, children, ...rest }, ref) => {
    const [openValues, setOpenValues] = React.useState<string[]>(() => {
      if (defaultValue === undefined) return [];
      return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
    });

    const toggle = React.useCallback(
      (value: string) => {
        setOpenValues((prev) => {
          const isOpen = prev.includes(value);
          if (type === 'single') {
            if (isOpen) return collapsible ? [] : prev;
            return [value];
          }
          return isOpen ? prev.filter((v) => v !== value) : [...prev, value];
        });
      },
      [type, collapsible],
    );

    const ctx = React.useMemo<AccordionContextValue>(
      () => ({ type, openValues, toggle, collapsible }),
      [type, openValues, toggle, collapsible],
    );

    return (
      <AccordionContext.Provider value={ctx}>
        <div ref={ref} className={className} {...rest}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  },
);
Accordion.displayName = 'Accordion';

interface AccordionItemContextValue {
  value: string;
  isOpen: boolean;
}
const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(null);

function useAccordionItem() {
  const ctx = React.useContext(AccordionItemContext);
  if (!ctx) throw new Error('AccordionTrigger/Content must be used inside <AccordionItem>.');
  return ctx;
}

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, className, children, ...rest }, ref) => {
    const { openValues } = useAccordion();
    const isOpen = openValues.includes(value);
    const itemCtx = React.useMemo(() => ({ value, isOpen }), [value, isOpen]);
    return (
      <AccordionItemContext.Provider value={itemCtx}>
        <div ref={ref} className={cn('border-b', className)} data-state={isOpen ? 'open' : 'closed'} {...rest}>
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  },
);
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...rest }, ref) => {
  const { toggle } = useAccordion();
  const { value, isOpen } = useAccordionItem();
  return (
    <h3 className="flex">
      <button
        ref={ref}
        type="button"
        aria-expanded={isOpen}
        data-state={isOpen ? 'open' : 'closed'}
        onClick={() => toggle(value)}
        className={cn(
          'flex flex-1 items-center justify-between py-4 text-left text-sm font-medium transition-all hover:underline',
          className,
        )}
        {...rest}
      >
        {children}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>
    </h3>
  );
});
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...rest }, ref) => {
  const { isOpen } = useAccordionItem();
  if (!isOpen) return null;
  return (
    <div
      ref={ref}
      data-state="open"
      className={cn('overflow-hidden text-sm', className)}
      {...rest}
    >
      <div className="pb-4 pt-0">{children}</div>
    </div>
  );
});
AccordionContent.displayName = 'AccordionContent';

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
